/**
 * THE MONTHLY STEERING PACK, COMPOSED FROM THE PORTFOLIO — the produced document
 * that turns the live portfolio into the pack a steering committee reads.
 *
 * WHY THIS EXISTS. The Portfolio screen reads every project's pulse on demand
 * (src/portfolio.ts) — health, budget, blockers, what moved. What a committee
 * meets over is the same reading, but PRODUCED: a branded A4 file, decisions
 * first, one page per project, that lands in the minutes and the inbox. Until now
 * that pack was rebuilt by hand in a slide deck every month, drifting from the
 * board the moment it was pasted. This composes it from the rows the portfolio
 * already derives, so the pack and the screen can never disagree.
 *
 * TAKES WHAT IT PRINTS, AND DERIVES NOTHING THE SCREEN DOES NOT. Everything here is
 * a rearranging of `PortfolioRow` values the portfolio has already worked out — the
 * counts, the budget totals, the decisions (the decision-type waiting items), the
 * milestone lateness (an active phase past its own planned end). The one computed
 * reading is the cadence forecast on the milestone table, and it is not invented
 * here either: it is the SAME `withMilestoneForecasts` enrichment the Portfolio
 * screen applies, run over the cards the caller hands in (0215), so the pack's
 * predicted date is the one the screen shows. With no cards handed in it falls back
 * to the plan forecast and computes nothing. The generator (portfolio-pack-pdf.ts)
 * draws this frozen model and reaches for nothing, so the same portfolio twice is
 * the same file — which is what a document filed in the minutes has to be.
 *
 * PURE, LIKE composeRequirements. No store, no React, no clock beyond the `asOf`
 * it is handed — so the whole of "given these rows, what does the pack say" is
 * proven on plain fixtures.
 */
import type { PortfolioRow, Rag } from './portfolio';
import { type Phase } from './phases';
import { LESSON_KIND_LABELS } from './lessons';
import { fmtMoney } from './money';
import { healthTrend, RAG_WORD, type Pulse } from './portfolio-trend';
import {
  projectMilestones,
  goLiveCollisions,
  effDay,
  slipSeries,
  type Milestone,
  type MilestoneRow,
  type MilestonePulse,
  type MilestoneState,
} from './milestones';
import { withMilestoneForecasts } from './milestone-forecast';
import type { Issue } from './types';
import type { WorkCalendar } from './work-calendar';

/** A grade the pack draws a square for — the three RAG grades, plus `hold` (its
 *  own hushed state) and `grey` (a signal with nothing to judge, e.g. no baseline). */
export type PackRag = Rag | 'hold' | 'grey';

/** One health bucket on page 1 — a grade, its word, and how many projects sit in it. */
export interface HealthCount {
  rag: PackRag;
  label: string;
  n: number;
}

/** A decision the committee is being asked to make — the project, the ask (the
 *  decision-type waiting item's own words) and the one-line consequence beneath it. */
export interface PackDecision {
  project: string;
  ask: string;
  consequence: string;
  /** The card ref(s) carrying this decision, so the committee can find "which
   *  one" — the same ask on several cards is ONE decision, listed once, and this
   *  names the cards behind it (e.g. "#141, #206"). Empty when a card has no ref. */
  refs: string[];
}

/** The figures a burn bar draws from — all in the workspace base currency, whole
 *  units. `paid` and `committed` are DISJOINT (paid = settled, committed = raised
 *  but unpaid), exactly as the ledger keeps them, so they stack rather than overlap. */
export interface PackBudget {
  base?: number;
  forecast?: number;
  variance?: { diff: number; pct: number };
  paid: number;
  committed: number;
}

/** One row of the "portfolio at a glance" table — a single RAG mark and status
 *  word per project (Vinnie: one mark, not a split), plus the headline figures. */
export interface GlanceRow {
  name: string;
  owner: string;
  rag: PackRag;
  statusWord: string;
  /** The progress percent, or null on a held project (its bar is hushed). */
  progress: number | null;
  /** The go-live day, formatted, or "Paused" on a held project. */
  goLive: string;
  /** Days the go-live slips (0 = on plan) and whether that slip is red (> 21). */
  slipDays: number;
  slipRed: boolean;
  /** Who/what the project is waiting on, in a few words, or "" for nothing. */
  waitingOn: string;
  /** The health trend vs last month (0122), or undefined when parked or with no
   *  pulse history yet — the Trend column then reads nothing. */
  trend?: GlanceTrend;
}

/** The pack's month-over-month trend cell: an arrow direction (none on a first
 *  pulse) and the word it reads. Wording is the pack's own — "held N wks", not
 *  the roll-up's "stuck · N wks". */
export interface GlanceTrend {
  arrow: 'up' | 'down' | 'flat' | null;
  text: string;
}

/** How far back "last month" reaches for the pack's trend read. */
const PACK_PRIOR_DAYS = 28;

/**
 * THE PACK'S TREND CELL — the same derivation the screen uses (healthTrend),
 * read against last month rather than last week, worded for the pack. Undefined
 * when the project is parked or has no pulse to compare (nothing to draw); "New"
 * on a first pulse; "held N wks" once an amber/red grade has held two weeks or
 * more; "steady" otherwise; "from At risk"/"from Off track" on a change.
 */
export function glanceTrend(pulses: Pulse[]): GlanceTrend | undefined {
  const t = healthTrend(pulses, { priorDays: PACK_PRIOR_DAYS });
  if (t.kind === 'none' || t.kind === 'hold') return undefined;
  if (t.kind === 'new') return { arrow: null, text: 'New' };
  if (t.dir === 'flat') {
    return { arrow: 'flat', text: t.to !== 'green' && t.stuckWeeks >= 2 ? `held ${t.stuckWeeks} wks` : 'steady' };
  }
  return { arrow: t.dir, text: `from ${RAG_WORD[t.from]}` };
}

/** One of the three sub-signals on a detail page — its square, its cap, one line. */
export interface PackSignal {
  rag: PackRag;
  cap: string;
  reason: string;
}

/** One milestone row as the pack draws it — the planned → forecast → actual read
 *  with a slip, mirroring the on-screen milestone table (src/milestones.ts derives
 *  the state and slip; this is only how a committee reads it). */
export interface PackMilestone {
  /** The milestone's name. The portfolio-level "landing this cycle" table prefixes
   *  it with "Project — "; a project's own detail page shows the bare name. */
  name: string;
  planned: string;
  forecast: string;
  actual: string;
  /** "on plan" / "on time" / "+7d". */
  slip: string;
  slipTone: 'g' | 'a' | 'r';
  /** Whether the slip carries the rising arrow (a real slip, not on-plan). */
  slipArrow: boolean;
  /** The diamond's colour, and whether it is a ringed go-live. */
  diamondTone: 'g' | 'a' | 'r';
  golive: boolean;
  /** The cadence PREDICTION, when the milestone gates work and the board has
   *  enough history (0215) — the Forecast column then reads this p50 ("~5 Nov")
   *  instead of the plan date, and `confidence` is the 85% date beneath it
   *  ("85% by 12 Nov"). Both absent when there is no prediction, so the column
   *  falls back to the plan forecast exactly as before. */
  predicted?: string;
  confidence?: string;
}

/** One waiting-on line on a detail page. */
export interface PackWaiting {
  who: string;
  ask: string;
  age: string;
  ageOld: boolean;
  /** How many cards carry this same who + ask — 1 for a lone item, >1 when the
   *  identical ask sits on several cards (drawn as "×N"), so the page reads a real
   *  list rather than the same sentence stacked. */
  count: number;
}

/** One risk off the project's charter register, as the pack shows it — read
 *  straight from the register (charter.ts) and relocated here. The pack reports
 *  what was captured and how it is being handled; it does not judge severity. */
export interface PackRisk {
  risk: string;
  likelihood: string;
  impact: string;
  /** The derived severity band (Critical/High/Medium/Low) from the register's one L×I scale —
   *  the figure the pack shows, not the impact word (Q2). '' when unassessed. */
  severity: string;
  owner: string;
  mitigation: string;
}

/** One project's steering-detail page. */
export interface PackProject {
  name: string;
  owner: string;
  rag: PackRag;
  statusWord: string;
  verdict: string;
  schedule: PackSignal;
  budget: PackSignal;
  blockers: PackSignal;
  milestones: PackMilestone[];
  budgetFigures: PackBudget;
  waiting: PackWaiting[];
  /** The decision-type waiting item(s), joined — absent when the project has none. */
  decision?: string;
  moved: string;
  /** The charter Risk register, so the steering meeting sees captured risks and
   *  their mitigations rather than leaving them in the document. Empty when the
   *  charter carries no register. The PDF caps how many it draws. */
  risks: PackRisk[];
}

/**
 * ONE DELIVERY EVENT — a board that crossed from delivery into service this cycle
 * (#435). The as-delivered record was frozen at the handoff (DeliverySnapshot); the
 * pack re-words it and derives nothing the snapshot did not already hold, so the pack
 * and the board's History can never disagree about what shipped.
 */
export interface PackDeliveryEvent {
  project: string;
  /** "18 Sept" — the day the board actually went live. */
  wentLive: string;
  /** "12 Sept", or '' when the project set no planned go-live to compare against. */
  planned: string;
  /** The schedule variance frozen at go-live — "On time" / "+6d late" / "10d early",
   *  or '' when there was no planned date. Day-granular, the same read the on-screen
   *  delivery-event callout shows. */
  schedule: string;
  scheduleTone: 'good' | 'bad' | 'plain';
  /** "12 of 16 cards shipped, 4 deferred" — scope shipped vs deferred at go-live. */
  scope: string;
  /** The budget total at go-live, formatted ("$402k"), or '' when none was carried. */
  budget: string;
  /** True when the handoff was provisional — a delivery phase was still open, so the
   *  snapshot is the honest "not fully ready yet" variant. */
  provisional: boolean;
}

/** The whole frozen pack, ready to draw. */
export interface PackModel {
  workspaceName: string;
  /** "September 2026" — the month the pack covers, from `asOf`. */
  period: string;
  /** How many boards are reviewed as projects (the tracked ones). */
  projectCount: number;
  /** How many boards were left out because they are not run as projects (no plan,
   *  no phases, no budget) — stated on page 1 so the exclusion is visible. */
  notTrackedCount: number;
  /** Of the reviewed, non-held projects: how many carry a budget, and how many
   *  were counted for the budget total — so "N of M costed" can qualify the figure. */
  budgetedCount: number;
  costedCount: number;
  /** The page-1 verdict paragraph — the portfolio in one read. */
  verdict: string;
  health: HealthCount[];
  decisions: PackDecision[];
  budget: PackBudget;
  glance: GlanceRow[];
  /** The portfolio-level "Milestones landing this cycle" table — every open
   *  milestone across every project, soonest first, each named "Project — name". */
  cycleMilestones: PackMilestone[];
  /** The one-line read beneath that table — a go-live collision and the biggest
   *  drift, the two things a steering meeting acts on. Absent when neither applies. */
  milestoneReading?: string;
  projects: PackProject[];
  /** WHAT WENT LIVE THIS CYCLE (#435) — the boards that crossed from delivery into
   *  service in the month the pack covers, newest first; empty when none did. */
  deliveryEvents: PackDeliveryEvent[];
  /** LEARNING & CLOSURE (#491) — the projects that reached close this cycle (a
   *  signed after-action report), newest first; empty when none closed. */
  closed: PackClosure[];
  /** The recommendation-bearing lessons raised across the portfolio this cycle,
   *  newest first, capped to what the page shows. */
  lessonsForward: PackLessonForward[];
  /** The full count of this cycle's forward lessons, for the "…and N more" note —
   *  so a capped list still says how much it stands for. */
  lessonsForwardTotal: number;
}

/**
 * ONE CLOSED PROJECT on the Learning & closure page — composed from a signed
 * after-action report, which froze its own numbers at close (AarVariance). The
 * pack re-words them; it derives nothing the report did not already hold.
 */
export interface PackClosure {
  project: string;
  /** "Closed · delivered" or "Closed · delivered late" — ok vs warn. */
  pillWord: string;
  pillOk: boolean;
  /** The after-action outcome in a line, with the benefits tally appended. */
  outcome: string;
  /** "On time" / "2 wks early" / "+6 wks late". */
  schedule: string;
  scheduleTone: 'good' | 'bad' | 'plain';
  /** "£398k · 5% under" / "£1.21m · on budget" — absent when no budget was set. */
  cost?: string;
  costTone: 'good' | 'bad' | 'plain';
  lessonsLogged: number;
  /** The single lesson the committee should carry — the report's recommendation. */
  carryForward?: string;
}

/** One lesson being carried forward — a recommendation-bearing lesson raised this
 *  cycle, worded for the pack. */
export interface PackLessonForward {
  title: string;
  /** The app's own kind word — "What worked" / "What didn't". */
  kindLabel: string;
  recommendation: string;
  /** The project it was raised on, or "Workspace" for a workspace-wide lesson. */
  source: string;
}

/**
 * WHAT THE LEARNING SECTION IS HANDED, pre-flattened by the caller so the composer
 * reads no document and no rich text — the same contract as charter risks (lifted
 * to rows before the pure layer ever runs). The composer windows these to the
 * cycle, orders them and words them.
 */
export interface PackClosureInput {
  project: string;
  /** When the after-action report was signed/frozen — the close moment. */
  closedAt: number;
  /** The "what we delivered" narrative, flattened to running text. */
  outcome: string;
  /** The report's frozen schedule slip in days (actual − planned go-live). */
  scheduleDays?: number;
  budgetBaseline?: number;
  forecast?: number;
  /** How the expected benefits stood at close. */
  benefits?: { realised: number; partial: number; notyet: number };
  /** How many lessons the report logged. */
  lessonsLogged: number;
  /** The report's top recommendation, flattened. */
  carryForward?: string;
}

/** One recommendation-bearing lesson handed to the pack, pre-flattened. */
export interface PackLessonInput {
  createdAt: number;
  title: string;
  recommendation: string;
  kind: 'worked' | 'didnt';
  source: string;
}

/** The learning inputs the composer takes beyond the rows. */
export interface PackLearning {
  closures?: PackClosureInput[];
  lessons?: PackLessonInput[];
}

/** What the composer needs beyond the rows: whose workspace, and the reference
 *  instant the month label and any "days behind" are measured against. */
export interface PackMeta {
  workspaceName: string;
  asOf: number;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
// The short worded month is the app's one list ("Sept", not "Sep" — R16).
import { MONTHS as SHORT } from './when';

/** "September 2026" from an instant. */
function monthLabel(at: number): string {
  const d = new Date(at);
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** A plain `YYYY-MM-DD` day as "18 Jul". Blank stays blank. */
export function fmtDay(day?: string | null): string {
  if (!day) return '';
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return '';
  return `${d} ${SHORT[m - 1]}`;
}

/** A plural helper — "1 project" / "3 projects". */
const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/** The grade a project's pill shows — its health, or `hold` when parked. */
const ragOf = (r: PortfolioRow): PackRag => (r.onHold ? 'hold' : (r.health ?? 'green'));

const HEALTH_WORD: Record<PackRag, string> = {
  green: 'On track',
  amber: 'At risk',
  red: 'Off track',
  hold: 'On hold',
  grey: 'Not set',
};

/** Sentence-cap the first letter of a derived reason (the signals speak lower-case). */
const cap = (s: string): string => (s ? s[0].toUpperCase() + s.slice(1) : s);

/** A short word for who a waiting item waits on, from its type and named target. */
function waitingWho(type: string, target?: string): string {
  if (type === 'decision') return 'A decision';
  if (target) return target;
  const words: Record<string, string> = {
    person: 'A person',
    external: 'Someone outside the team',
    test: 'A test',
    design: 'Design',
    bundled: 'Bundled work',
    parked: 'Parked',
    item: 'Another item',
    other: 'Something else',
  };
  return words[type] ?? 'Waiting';
}

/** The one-word/short "waiting on" for the glance table — the project's oldest
 *  open item, in a few words, or "" when nothing is open. */
function glanceWaiting(r: PortfolioRow): string {
  if (r.waiting.length === 0) return '';
  const w = r.waiting[0];
  return waitingWho(w.type, w.target);
}

/** State → the diamond's colour, the same mapping the on-screen table uses: a
 *  settled (done) or on-track milestone is green, slipping amber, late red. */
const MS_TONE: Record<MilestoneState, 'g' | 'a' | 'r'> = {
  'on-track': 'g',
  slipping: 'a',
  late: 'r',
  done: 'g',
};

/** How a milestone's slip reads in the Slip column — mirrors src/components/…/
 *  milestone.tsx slipReadout so the pack and the screen never disagree. Done-but-
 *  late keeps its amber arrow (it landed late); a still-open late one is red. */
function packSlip(m: Milestone): { text: string; tone: 'g' | 'a' | 'r'; arrow: boolean } {
  if (m.state === 'done')
    return m.slipDays > 0 ? { text: `+${m.slipDays}d`, tone: 'a', arrow: true } : { text: 'on time', tone: 'g', arrow: false };
  if (m.state === 'on-track') return { text: 'on plan', tone: 'g', arrow: false };
  return { text: `+${m.slipDays}d`, tone: m.state === 'late' ? 'r' : 'a', arrow: true };
}

/** The Forecast cell: a late milestone with no re-forecast has no new date — a dash,
 *  not the stale plan date (which would contradict its "+Nd" slip). */
function forecastCell(m: Milestone): string {
  if (m.state === 'late' && m.forecastDay === m.plannedDay) return '—';
  return fmtDay(m.forecastDay) || '—';
}

/** A derived milestone → the pack's drawn row. `label` overrides the name (the
 *  portfolio-level table prefixes it with the project). */
function packMilestone(m: Milestone, label?: string): PackMilestone {
  // When the milestone gates work and the board has enough history, the Forecast
  // column leads with the 85%-confident date (p85) — the same headline the portfolio
  // screen now shows everywhere — with the 50/50 midpoint (p50) beneath it as context.
  // The milestone set must have been enriched (withMilestoneForecasts) for this to be
  // present; without it the column falls back to the plan forecast.
  const f = m.forecast;
  const predicted = f && f.ready ? fmtDay(msToDay(f.p85)) : undefined;
  // Compact for the PDF's narrow Forecast column: the main date IS the 85% figure, the
  // sub-line gives the 50/50 midpoint. (The screen has room for the fuller wording.)
  const confidence = f && f.ready ? `85% · 50/50 ~${fmtDay(msToDay(f.p50))}` : undefined;
  // Slip measured against the SAME p85 the Forecast column shows (mirrors the screen's
  // milestone table, src/components/portfolio/milestone.tsx), so the predicted date and
  // its slip never disagree. A done row keeps its actual-vs-plan slip; an un-forecast row
  // falls back to the plan/state slip.
  const planMsUtc = m.plannedDay ? Date.parse(`${m.plannedDay}T00:00:00Z`) : undefined;
  const predSlip = f && f.ready && planMsUtc !== undefined && m.state !== 'done'
    ? Math.max(0, Math.round((f.p85 - planMsUtc) / 86_400_000))
    : undefined;
  const s: { text: string; tone: 'g' | 'a' | 'r'; arrow: boolean } = predSlip !== undefined
    ? (predSlip > 0
        ? { text: `+${predSlip}d`, tone: predSlip > 21 ? 'r' : 'a', arrow: true }
        : { text: 'on plan', tone: 'g', arrow: false })
    : packSlip(m);
  return {
    name: label ?? m.name,
    planned: fmtDay(m.plannedDay) || '—',
    forecast: predicted ?? forecastCell(m),
    actual: m.actualDay ? fmtDay(m.actualDay) || '—' : '—',
    slip: s.text,
    slipTone: s.tone,
    slipArrow: s.arrow,
    diamondTone: MS_TONE[m.state],
    golive: m.kind === 'golive',
    predicted,
    confidence,
  };
}

/** The Mon–Sun range of the ISO week a set of colliding milestones land in, as
 *  "8–14 Dec" (or "28 Nov – 4 Dec" across a month), for the reading line. */
function weekRange(ms: Milestone[]): string {
  const days = ms.map((m) => m.forecastDay ?? m.plannedDay).filter(Boolean) as string[];
  if (days.length === 0) return '';
  const [y, mo, d] = days.sort()[0].split('-').map(Number);
  const at = Date.UTC(y, mo - 1, d);
  const dow = (new Date(at).getUTCDay() + 6) % 7; // 0 = Monday
  const mon = new Date(at - dow * 86_400_000);
  const sun = new Date(mon.getTime() + 6 * 86_400_000);
  const day = (x: Date) => x.getUTCDate();
  const mon3 = (x: Date) => SHORT[x.getUTCMonth()];
  return mon.getUTCMonth() === sun.getUTCMonth()
    ? `${day(mon)}–${day(sun)} ${mon3(sun)}`
    : `${day(mon)} ${mon3(mon)} – ${day(sun)} ${mon3(sun)}`;
}

/**
 * THE MILESTONE READING LINE — the one sentence a steering meeting acts on beneath
 * the "landing this cycle" table: a go-live collision (two projects going live in
 * one week — "move one"), and the single biggest drift, with how many daily pulses
 * it has been climbing when that history is recorded.
 */
function milestoneReadingOf(all: Milestone[], pulsesByBoard?: Map<string, MilestonePulse[]>): string | undefined {
  const parts: string[] = [];
  const collisions = goLiveCollisions(all);
  if (collisions.length) {
    const c = collisions[0];
    const names = c.milestones.map((m) => m.projectName).join(', ');
    parts.push(`${plural(c.milestones.length, 'go-live')} fall in the week of ${weekRange(c.milestones)} (${names}).`);
  }
  const drifting = all
    .filter((m) => (m.state === 'slipping' || m.state === 'late') && m.slipDays > 0)
    .sort((a, b) => b.slipDays - a.slipDays)[0];
  if (drifting) {
    const pulses = (pulsesByBoard?.get(drifting.boardId) ?? []).filter((p) => p.milestoneKey === drifting.key);
    const series = slipSeries(pulses, drifting.plannedDay);
    const climbed = series.length >= 2 ? ` across the last ${plural(series.length, 'pulse')}` : '';
    parts.push(`${drifting.projectName} — ${drifting.name} has drifted ${plural(drifting.slipDays, 'day')}${climbed}.`);
  }
  return parts.length ? parts.join(' ') : undefined;
}

/** The split-RAG schedule signal, worded for the pack. */
function scheduleSignalOf(r: PortfolioRow): PackSignal {
  const reason =
    r.slipDays > 0 && r.forecastDay
      ? `Go-live slips ${plural(r.slipDays, 'day')}, to ${fmtDay(r.forecastDay)}.`
      : `${cap(r.schedule.why)}.`;
  return { rag: r.schedule.rag, cap: 'Schedule', reason };
}

/** The split-RAG budget signal — grey with a plain note when there is no baseline. */
function budgetSignalOf(r: PortfolioRow): PackSignal {
  if (!r.budget) return { rag: 'grey', cap: 'Budget', reason: 'No baseline set.' };
  const money =
    r.variance && r.budgetBase ? ` (${fmtMoney(Math.abs(r.variance.diff))}).` : '.';
  const word = r.budget.why.includes('over')
    ? `${r.budget.why} baseline${money}`
    : r.budget.why.includes('under')
      ? `${r.budget.why} baseline${money}`
      : `${cap(r.budget.why)}.`;
  return { rag: r.budget.rag, cap: 'Budget', reason: cap(word) };
}

/** The split-RAG blockers signal. */
function blockersSignalOf(r: PortfolioRow): PackSignal {
  const open = r.waiting.length;
  const reason = open === 0 ? 'Nothing open.' : `${cap(r.blockers.why)}.`;
  return { rag: r.blockers.rag, cap: 'Blockers', reason };
}

/** The portfolio-level budget totals — summed over the rows that carry each figure. */
function portfolioBudget(rows: PortfolioRow[]): PackBudget {
  let base = 0;
  let forecast = 0;
  let paid = 0;
  let committed = 0;
  let anyBase = false;
  let anyForecast = false;
  for (const r of rows) {
    if (r.budgetBase != null) {
      base += r.budgetBase;
      anyBase = true;
      // A project with a baseline but no forecast lands on its baseline.
      forecast += r.forecast ?? r.budgetBase;
      anyForecast = anyForecast || r.forecast != null;
    }
    paid += r.actual;
    committed += r.committed;
  }
  const variance =
    anyBase && base > 0 ? { diff: forecast - base, pct: ((forecast - base) / base) * 100 } : undefined;
  return {
    base: anyBase ? base : undefined,
    forecast: anyBase || anyForecast ? forecast : undefined,
    variance,
    paid,
    committed,
  };
}

/** The project's own budget figures for its detail page. */
function projectBudget(r: PortfolioRow): PackBudget {
  return {
    base: r.budgetBase,
    forecast: r.forecast ?? r.budgetBase,
    variance: r.variance,
    paid: r.actual,
    committed: r.committed,
  };
}

/** A money figure to one decimal million/thousand for the tight budget captions —
 *  "$1.2M", "$402k" — falling back to the plain formatter for small amounts. */
export function fmtMoneyShort(n?: number | null): string {
  if (n == null || !Number.isFinite(n)) return '';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  const sym = fmtMoney(0).replace(/0.*$/, ''); // the active currency symbol
  if (abs >= 1_000_000) return `${sign}${sym}${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 10_000) return `${sign}${sym}${Math.round(abs / 1000)}k`;
  if (abs >= 1000) return `${sign}${sym}${(abs / 1000).toFixed(1)}k`;
  return fmtMoney(n);
}

/** The one-line consequence under a committee decision — status, the sharpest
 *  driver, and who owns it. */
function decisionConsequence(r: PortfolioRow): string {
  const bits: string[] = [HEALTH_WORD[ragOf(r)].toLowerCase()];
  if (r.slipDays > 0) bits.push(`go-live ${plural(r.slipDays, 'day')} late`);
  if (r.variance && r.variance.diff > 0) {
    bits.push(`forecast ${fmtMoneyShort(r.variance.diff)} over`);
  }
  return `${cap(bits.join(' · '))}. Owner: ${r.owner}.`;
}

/**
 * THE COMMITTEE DECISIONS — one line per DISTINCT ask, never the same sentence
 * repeated. A project can carry the identical decision-type blocker on many cards;
 * those are one decision, named once, with the cards behind it so "which one?" is
 * answerable. A PARKED project raises nothing — the decision to park it has already
 * been made — so on-hold projects are skipped. Worst-health projects first (the
 * rows arrive worst-first), and their own asks in the order they were raised.
 */
function committeeDecisions(rows: PortfolioRow[]): PackDecision[] {
  const out: PackDecision[] = [];
  for (const r of rows) {
    if (r.onHold) continue;
    // Group this project's decision-type asks by their wording, keeping the cards.
    const byAsk = new Map<string, { ask: string; refs: string[] }>();
    for (const w of r.waiting) {
      if (w.type !== 'decision') continue;
      const ask = w.label.trim();
      if (!ask) continue;
      const key = ask.toLowerCase();
      const seen = byAsk.get(key);
      if (seen) {
        if (w.ref && !seen.refs.includes(w.ref)) seen.refs.push(w.ref);
      } else {
        byAsk.set(key, { ask, refs: w.ref ? [w.ref] : [] });
      }
    }
    for (const { ask, refs } of byAsk.values()) {
      out.push({ project: r.name, ask, consequence: decisionConsequence(r), refs });
    }
  }
  return out;
}

/** The per-project verdict paragraph — owner, standing, and the reason in its own
 *  derived words. */
function projectVerdict(r: PortfolioRow): string {
  const word = HEALTH_WORD[ragOf(r)].toLowerCase();
  if (r.onHold) {
    const why = r.holdReason?.trim();
    return `${r.owner} owns this project. It is on hold${why ? ` — ${why}` : ''}.`;
  }
  const tail = r.health && r.health !== 'green' && r.healthWhy ? ` — ${r.healthWhy}` : '';
  const lead = `${r.owner} owns this project. It is ${word}${tail}.`;
  // A second sentence names the sharpest live signal, when there is one.
  if (r.slipDays > 0 && r.forecastDay) {
    return `${lead} The go-live now forecasts ${plural(r.slipDays, 'day')} late, to ${fmtDay(r.forecastDay)}.`;
  }
  if (r.waiting.length > 0) {
    const w = r.waiting[0];
    return `${lead} It is waiting on ${waitingWho(w.type, w.target).toLowerCase()}.`;
  }
  return lead;
}

/**
 * THE PER-PROJECT WAITING-ON LEDGER, DEDUPED — a project can carry the identical
 * ask on many cards ("the battery test — cannot be signed off" on six of them); the
 * committee reads one line with a ×N count and the OLDEST age, not the same sentence
 * six times. Grouped by who + ask (ask case-insensitive), first-seen order kept so
 * the oldest open item still leads (r.waiting arrives oldest-first).
 */
function projectWaiting(r: PortfolioRow): PackWaiting[] {
  const byKey = new Map<string, { who: string; ask: string; days: number | null; count: number }>();
  for (const w of r.waiting) {
    const who = waitingWho(w.type, w.target);
    const ask = w.label.trim();
    const key = `${who} ${ask.toLowerCase()}`;
    const seen = byKey.get(key);
    if (seen) {
      seen.count += 1;
      if (w.days != null && (seen.days == null || w.days > seen.days)) seen.days = w.days;
    } else {
      byKey.set(key, { who, ask, days: w.days ?? null, count: 1 });
    }
  }
  return [...byKey.values()].map((g) => ({
    who: g.who,
    ask: g.ask,
    age: g.days != null ? plural(g.days, 'day') : '',
    ageOld: (g.days ?? 0) > 7,
    count: g.count,
  }));
}

/** The decision text for a detail page — the DISTINCT decision-type asks, joined.
 *  The same decision on many cards is one ask, not the sentence repeated behind
 *  "Also:" (the page-1 committee list dedupes the same way). */
function projectDecision(r: PortfolioRow): string | undefined {
  const seen = new Set<string>();
  const asks: string[] = [];
  for (const w of r.waiting) {
    if (w.type !== 'decision') continue;
    const ask = w.label.trim();
    if (!ask) continue;
    const key = ask.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    asks.push(ask);
  }
  return asks.length ? asks.join(' Also: ') : undefined;
}

/** "Moved this month" as one line, or the honest "No change" note. */
function movedLine(r: PortfolioRow): string {
  return r.moved.length ? `${r.moved.join('; ')}.` : 'No change recorded this month.';
}

/**
 * COMPOSE THE PACK — the portfolio rows (already sorted worst-first by
 * buildPortfolio) become the frozen model the generator draws. Pure and
 * deterministic: the same rows and `asOf` produce the same model, byte for byte.
 */
export function composePortfolioPack(
  rows: PortfolioRow[],
  meta: PackMeta,
  // The daily pulses by board id (0122), for the glance Trend column. Optional —
  // a caller with no history yet composes a pack with no trend cells.
  pulsesByBoard?: Map<string, Pulse[]>,
  // The new milestone model (0124): each board's phases, its standalone milestone
  // rows, and its daily forecast snapshots. Optional — with none, every project's
  // milestone set is just its go-live, derived from the row's own schedule.
  milestoneData?: {
    phasesByBoard?: Map<string, Phase[]>;
    standaloneByBoard?: Map<string, MilestoneRow[]>;
    pulsesByBoard?: Map<string, MilestonePulse[]>;
    // The board's cards, so a milestone that gates work carries the cadence
    // forecast the screen shows (0215). Optional — with none, the milestone table
    // reads the plan forecast exactly as before, and the pack still can't disagree
    // with a screen that was also given no cards.
    issuesByBoard?: Map<string, Issue[]>;
    // The workspace working calendar (0216), so the pack's predicted dates skip
    // known future non-working days exactly as the screen's do.
    calendar?: WorkCalendar;
  },
  // LEARNING & CLOSURE (#491): the signed after-action reports and the lessons
  // raised this cycle, pre-flattened. Optional — a pack composed with none simply
  // carries no Learning & closure page.
  learning?: PackLearning,
): PackModel {
  const asOf = meta.asOf;

  // A steering pack reviews PROJECTS — boards actually run as one (a plan, phases,
  // or a budget). A bare development/kanban board tracks work but is not a project
  // the committee reviews, so it is kept out of the whole pack: the health counts,
  // the budget total, the glance, the milestone table and the detail pages. The
  // count of what was left out is stated on page 1, so nothing is silently dropped.
  const projectRows = rows.filter((r) => r.tracked);
  const notTrackedCount = rows.length - projectRows.length;

  // Each project's milestones, derived once (the go-live + any promoted phase ends
  // + standalone points), so the per-project tables and the portfolio-level
  // "landing this cycle" table read from the exact same set.
  const milestonesByProject = new Map<string, Milestone[]>();
  for (const r of projectRows) {
    const set = projectMilestones(
      r,
      milestoneData?.phasesByBoard?.get(r.id) ?? [],
      milestoneData?.standaloneByBoard?.get(r.id) ?? [],
      asOf,
    );
    // Attach the cadence forecast where the board's cards were handed in, so the
    // Forecast column reads the same prediction the portfolio screen draws.
    const cards = milestoneData?.issuesByBoard?.get(r.id);
    milestonesByProject.set(r.id, cards ? withMilestoneForecasts(set, cards, asOf, undefined, milestoneData?.calendar) : set);
  }
  const allMilestones = projectRows.flatMap((r) => milestonesByProject.get(r.id) ?? []);

  // The portfolio-level table: every OPEN milestone across every project, soonest
  // first (an overdue one surfaces ahead of an upcoming one), each named with its
  // project. A done milestone has landed — it is not "landing this cycle".
  const cycleMilestones = allMilestones
    .filter((m) => m.state !== 'done' && effDay(m) != null)
    .sort((a, b) => (effDay(a) as number) - (effDay(b) as number) || a.projectName.localeCompare(b.projectName))
    .map((m) => packMilestone(m, `${m.projectName} — ${m.name}`));
  const milestoneReading = milestoneReadingOf(allMilestones, milestoneData?.pulsesByBoard);

  const counts: Record<PackRag, number> = { green: 0, amber: 0, red: 0, hold: 0, grey: 0 };
  for (const r of projectRows) counts[ragOf(r)] += 1;
  const health: HealthCount[] = [
    { rag: 'green', label: 'On track', n: counts.green },
    { rag: 'amber', label: 'At risk', n: counts.amber },
    { rag: 'red', label: 'Off track', n: counts.red },
    { rag: 'hold', label: 'On hold', n: counts.hold },
  ];

  // The budget total is the projects the committee is actually funding — a PARKED
  // project is not spending to plan, so its baseline is not blended into the
  // portfolio's "forecast vs baseline" (it read as a decision the meeting had
  // already made). Held projects keep their own detail page and figures.
  const budget = portfolioBudget(projectRows.filter((r) => !r.onHold));
  // How many of the reviewed projects carry a budget at all — so a small variance
  // is not read as the whole portfolio's when only a few projects are costed.
  const budgetedCount = projectRows.filter((r) => !r.onHold && r.budgetBase != null).length;
  const costedCount = projectRows.filter((r) => !r.onHold).length;

  const decisions = committeeDecisions(projectRows);

  const glance: GlanceRow[] = projectRows.map((r) => ({
    name: r.name,
    owner: r.owner,
    rag: ragOf(r),
    statusWord: HEALTH_WORD[ragOf(r)],
    progress: r.onHold ? null : r.progress,
    goLive: r.onHold ? 'Paused' : fmtDay(r.forecastDay ?? r.planDay) || '—',
    slipDays: r.slipDays,
    slipRed: r.slipDays > 21,
    waitingOn: glanceWaiting(r),
    trend: r.onHold ? undefined : glanceTrend(pulsesByBoard?.get(r.id) ?? []),
  }));

  const projects: PackProject[] = projectRows.map((r) => ({
    name: r.name,
    owner: r.owner,
    rag: ragOf(r),
    statusWord: HEALTH_WORD[ragOf(r)],
    verdict: projectVerdict(r),
    schedule: scheduleSignalOf(r),
    budget: budgetSignalOf(r),
    blockers: blockersSignalOf(r),
    milestones: (milestonesByProject.get(r.id) ?? []).map((m) => packMilestone(m)),
    budgetFigures: projectBudget(r),
    waiting: projectWaiting(r),
    decision: projectDecision(r),
    moved: movedLine(r),
    // Straight off the charter register (portfolio.ts → charter.ts) — relocated,
    // not re-judged. Empty when the charter carries no risks.
    risks: (r.charterRisks ?? []).map((k) => ({
      risk: k.risk,
      likelihood: k.likelihood,
      impact: k.impact,
      severity: k.severity,
      owner: k.owner,
      mitigation: k.mitigation,
    })),
  }));

  // WHAT WENT LIVE THIS CYCLE (#435). The boards that crossed from delivery into
  // service in the month the pack covers, newest first — read straight off each
  // board's frozen handoff snapshot (not just the tracked set, since a board that
  // went live is a project whatever it looks like now). Windowed on the handoff
  // moment, the same way closures window on the signing moment, so an event recorded
  // this cycle shows in this cycle's pack even if the go-live itself was logged late.
  const deliveryEvents = rows
    .filter((r) => r.deliverySnapshot != null && inPackMonth(r.deliverySnapshot.at, asOf))
    .sort((a, b) => b.deliverySnapshot!.at - a.deliverySnapshot!.at)
    .map(toDeliveryEvent);

  // LEARNING & CLOSURE (#491). The closures and lessons are windowed to the cycle
  // the pack covers — the calendar month of `asOf`, the same month its period reads
  // — and ordered newest first. A capped list of lessons keeps the page to a read,
  // with the full count carried so "…and N more" stays honest.
  const closed = (learning?.closures ?? [])
    .filter((c) => inPackMonth(c.closedAt, asOf))
    .sort((a, b) => b.closedAt - a.closedAt)
    .map(toClosure);
  const forwardAll = (learning?.lessons ?? [])
    .filter((l) => inPackMonth(l.createdAt, asOf))
    .sort((a, b) => b.createdAt - a.createdAt);
  const lessonsForward = forwardAll.slice(0, LESSONS_FORWARD_CAP).map(toLessonForward);

  return {
    workspaceName: meta.workspaceName,
    period: monthLabel(asOf),
    projectCount: projectRows.length,
    notTrackedCount,
    budgetedCount,
    costedCount,
    verdict: portfolioVerdict(projectRows, counts, budget),
    health,
    decisions,
    budget,
    glance,
    cycleMilestones,
    milestoneReading,
    projects,
    deliveryEvents,
    closed,
    lessonsForward,
    lessonsForwardTotal: forwardAll.length,
  };
}

/** How many forward lessons the Learning & closure page lists before it folds the
 *  rest into "…and N more" — enough to be useful, few enough to stay a read. */
const LESSONS_FORWARD_CAP = 6;

/** Whether an instant falls in the calendar month the pack covers. */
function inPackMonth(at: number, asOf: number): boolean {
  const a = new Date(at);
  const b = new Date(asOf);
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

/** The schedule verdict for a closure, from the report's frozen slip in days. */
function scheduleRead(days?: number): { text: string; tone: 'good' | 'bad' | 'plain' } {
  if (days == null) return { text: '—', tone: 'plain' };
  if (days <= -7) return { text: `${Math.round(-days / 7)} wks early`, tone: 'good' };
  if (days <= 0) return { text: 'On time', tone: 'good' };
  if (days >= 14) return { text: `+${Math.round(days / 7)} wks late`, tone: 'bad' };
  return { text: `+${days}d late`, tone: 'bad' };
}

/** The cost verdict — the final spend and how it landed against the baseline.
 *  Absent (no cost cell) when the project carried no budget. */
function costRead(baseline?: number, forecast?: number): { text?: string; tone: 'good' | 'bad' | 'plain' } {
  if (baseline == null || forecast == null) return { tone: 'plain' };
  const spend = fmtMoneyShort(forecast) || String(Math.round(forecast));
  const pct = baseline ? ((forecast - baseline) / baseline) * 100 : 0;
  if (Math.abs(pct) < 0.5) return { text: `${spend} · on budget`, tone: 'plain' };
  return { text: `${spend} · ${Math.abs(pct).toFixed(0)}% ${pct > 0 ? 'over' : 'under'}`, tone: pct > 0 ? 'bad' : 'good' };
}

/** Shape a closure input into what the page draws — the pill, the two verdicts and
 *  the outcome line with its benefits tally. */
function toClosure(c: PackClosureInput): PackClosure {
  const sch = scheduleRead(c.scheduleDays);
  const cost = costRead(c.budgetBaseline, c.forecast);
  const late = c.scheduleDays != null && c.scheduleDays > 0;
  // The delivered narrative, kept to a sentence or two, with the benefits tally
  // appended — the one closure question a steering committee asks ("did the value
  // land?"), stated in the pack rather than left in the report.
  let outcome = c.outcome.trim();
  if (outcome.length > 220) outcome = `${outcome.slice(0, 219).replace(/\s+\S*$/, '')}…`;
  if (c.benefits) {
    const total = c.benefits.realised + c.benefits.partial + c.benefits.notyet;
    if (total > 0) outcome = `${outcome} Benefits: ${c.benefits.realised} of ${total} realised.`;
  }
  return {
    project: c.project,
    pillWord: late ? 'Closed · delivered late' : 'Closed · delivered',
    pillOk: !late,
    outcome,
    schedule: sch.text,
    scheduleTone: sch.tone,
    cost: cost.text,
    costTone: cost.tone,
    lessonsLogged: c.lessonsLogged,
    carryForward: c.carryForward?.trim() || undefined,
  };
}

/** A ms instant as its plain UTC day "YYYY-MM-DD" — the same reading the portfolio
 *  uses for a go-live (portfolio.ts goLiveDayOf), so a date never parses two ways. */
const msToDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** The schedule variance a delivery event shows — day-granular, straight off the
 *  frozen `scheduleVarianceDays`, worded like the on-screen callout. Positive is
 *  late (the convention DeliverySnapshot stores); negative is early. */
function deliveryVariance(days?: number): { text: string; tone: 'good' | 'bad' | 'plain' } {
  if (days == null) return { text: '', tone: 'plain' };
  if (days === 0) return { text: 'On time', tone: 'good' };
  if (days < 0) return { text: `${-days}d early`, tone: 'good' };
  return { text: `+${days}d late`, tone: 'bad' };
}

/** Shape a row's frozen delivery snapshot into what the pack draws — re-wording the
 *  as-delivered record, deriving nothing new. Only called on rows that carry one. */
function toDeliveryEvent(r: PortfolioRow): PackDeliveryEvent {
  const s = r.deliverySnapshot!;
  const variance = deliveryVariance(s.scheduleVarianceDays);
  const scope =
    s.scopeTotal > 0
      ? `${s.scopeShipped} of ${s.scopeTotal} cards shipped${s.scopeDeferred > 0 ? `, ${s.scopeDeferred} deferred` : ''}`
      : 'No delivery cards tracked';
  return {
    project: r.name,
    wentLive: fmtDay(msToDay(s.actualGoLive)) || '—',
    planned: s.plannedGoLive != null ? fmtDay(msToDay(s.plannedGoLive)) : '',
    schedule: variance.text,
    scheduleTone: variance.tone,
    scope,
    budget: s.budgetAtGoLive != null ? (fmtMoneyShort(s.budgetAtGoLive) || String(Math.round(s.budgetAtGoLive))) : '',
    provisional: !!s.provisional,
  };
}

/** Shape a lesson input into a forward line — its kind word and its recommendation. */
function toLessonForward(l: PackLessonInput): PackLessonForward {
  return {
    title: l.title.trim(),
    kindLabel: LESSON_KIND_LABELS[l.kind],
    recommendation: l.recommendation.trim(),
    source: l.source,
  };
}

/**
 * THE PAGE-1 VERDICT — the portfolio in one paragraph, built only from the counts
 * and totals the pack already holds. Weight, not colour: it leads with what needs a
 * decision, then the shape of the rest, the budget position, and who is waiting.
 */
function portfolioVerdict(
  rows: PortfolioRow[],
  counts: Record<PackRag, number>,
  budget: PackBudget,
): string {
  const off = counts.red;
  const parts: string[] = [];

  const decisionCount = rows.reduce(
    (n, r) => n + r.waiting.filter((w) => w.type === 'decision').length,
    0,
  );

  if (off > 0) {
    parts.push(
      decisionCount > 0
        ? `${cap(plural(off, 'project'))} ${off === 1 ? 'is' : 'are'} off track and ${off === 1 ? 'needs' : 'need'} a decision from this committee.`
        : `${cap(plural(off, 'project'))} ${off === 1 ? 'is' : 'are'} off track.`,
    );
  } else {
    parts.push('No project is off track this month.');
  }

  const shape: string[] = [];
  if (counts.green > 0) shape.push(`${plural(counts.green, 'project')} on track`);
  if (counts.amber > 0) shape.push(`${counts.amber} at risk`);
  if (counts.hold > 0) shape.push(`${counts.hold} on hold`);
  if (shape.length) parts.push(`${cap(shape.join(', '))}.`);

  if (budget.base != null && budget.variance) {
    const v = budget.variance;
    const dir = v.diff > 0 ? 'over' : v.diff < 0 ? 'under' : 'on';
    if (dir === 'on') {
      parts.push(`The portfolio is forecast on its ${fmtMoneyShort(budget.base)} baseline.`);
    } else {
      parts.push(
        `The portfolio is forecast to land ${fmtMoneyShort(Math.abs(v.diff))} (${Math.abs(v.pct).toFixed(1)}%) ${dir} its ${fmtMoneyShort(budget.base)} baseline.`,
      );
    }
  }

  const waitingCount = rows.filter((r) => !r.onHold && r.waiting.length > 0).length;
  if (waitingCount > 0) {
    parts.push(`${cap(plural(waitingCount, 'project'))} ${waitingCount === 1 ? 'is' : 'are'} waiting on someone outside the team.`);
  }

  return parts.join(' ');
}
